import { useEffect, useRef } from "react";

/**
 * Manages browser / hardware back navigation for drill-down and overlay states.
 *
 * Uses a module-level handler stack so multiple concurrent uses (e.g. a drill-
 * down screen + an overlay slider opened inside it) work correctly:
 *   - Entering a state pushes a history marker + registers the exit handler.
 *   - Hardware / browser back fires ONLY the innermost (most recently pushed)
 *     handler — the outer drill-level handler is NOT triggered.
 *   - Programmatic close (state → false from UI) pops the history marker
 *     silently without firing any handler.
 */

// ─── module-level state ───────────────────────────────────────────────────────

const _stack: Array<() => void> = [];
let _listenerAttached = false;
// Counts programmatic history.back() calls so the popstate handler can skip them
let _programmaticCount = 0;

function _attachListener() {
    if (_listenerAttached || typeof window === "undefined") return;
    _listenerAttached = true;
    window.addEventListener("popstate", () => {
        if (_programmaticCount > 0) {
            _programmaticCount--;
            return;
        }
        if (_stack.length === 0) return;
        const handler = _stack.pop()!;
        handler();
    });
}

// ─── hook ─────────────────────────────────────────────────────────────────────

export function useDrillHistoryBack(isActive: boolean, onExit: () => void) {
    const onExitRef = useRef(onExit);
    const hasMarkerRef = useRef(false);

    // Keep latest callback without re-registering the handler
    useEffect(() => {
        onExitRef.current = onExit;
    }, [onExit]);

    // Stable handler that always calls the latest onExit
    const handlerRef = useRef(() => {
        hasMarkerRef.current = false;
        onExitRef.current();
    });

    // Ensure the single global listener is wired once
    useEffect(() => {
        _attachListener();
    }, []);

    // Push / pop history marker as isActive changes
    useEffect(() => {
        if (typeof window === "undefined") return;

        if (isActive && !hasMarkerRef.current) {
            const base =
                window.history.state && typeof window.history.state === "object"
                    ? window.history.state
                    : {};
            window.history.pushState({ ...base, __drillBackMarker: true }, "");
            hasMarkerRef.current = true;
            _stack.push(handlerRef.current);
            return;
        }

        if (!isActive && hasMarkerRef.current) {
            // Programmatic close — remove handler, pop the history entry silently
            hasMarkerRef.current = false;
            const idx = _stack.lastIndexOf(handlerRef.current);
            if (idx !== -1) _stack.splice(idx, 1);
            _programmaticCount++;
            window.history.back();
        }
    }, [isActive]);

    // Cleanup on unmount while still active (orphaned marker — matches original behaviour)
    useEffect(
        () => () => {
            if (hasMarkerRef.current) {
                hasMarkerRef.current = false;
                const idx = _stack.lastIndexOf(handlerRef.current);
                if (idx !== -1) _stack.splice(idx, 1);
            }
        },
        [],
    );
}

export default useDrillHistoryBack;
